import type { BlogPostItem } from './brandIntel';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const blogPostApi = {
  list: (workspaceId: string, params?: { brandId?: string; status?: string }) =>
    api
      .get<BlogPostItem[]>(`/api/workspaces/${workspaceId}/blog-posts`, { params })
      .then((r) => r.data),
  get: (workspaceId: string, blogPostId: string) =>
    api
      .get<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}`)
      .then((r) => r.data),
  approve: (workspaceId: string, blogPostId: string) =>
    api
      .post<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}/approve`)
      .then((r) => r.data),
  reject: (workspaceId: string, blogPostId: string) =>
    api
      .post<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}/reject`)
      .then((r) => r.data),
  regenerate: (workspaceId: string, blogPostId: string) =>
    api
      .post<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}/regenerate`)
      .then((r) => r.data),
  generate: (
    workspaceId: string,
    data: {
      brandId: string;
      topic?: string;
      customInstructions?: string;
      /** `"nl"` or `"en"`. Null/undefined = derive from brand onboarding
       *  profile (default backend behaviour). */
      language?: 'nl' | 'en';
    },
  ) =>
    api
      .post<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/generate`, data)
      .then((r) => r.data),
  createManual: (
    workspaceId: string,
    data: {
      brandId: string;
      title: string;
      content: string;
      focusKeyword?: string;
      metaDescription?: string;
      slug?: string;
      coverImageS3Key?: string;
      excerptOverride?: string;
    },
  ) =>
    api
      .post<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/manual`, data)
      .then((r) => r.data),
  update: (
    workspaceId: string,
    blogPostId: string,
    data: {
      title?: string;
      content?: string;
      focusKeyword?: string;
      metaDescription?: string;
      slug?: string;
      coverImageS3Key?: string;
      excerptOverride?: string;
    },
  ) =>
    api
      .patch<BlogPostItem>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}`, data)
      .then((r) => r.data),
  uploadImage: (workspaceId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ url: string; key: string }>(
        `/api/workspaces/${workspaceId}/blog-posts/upload-image`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      .then((r) => r.data);
  },
  // Synchronous AI-cover regeneration. Spinner-worthy — fal.ai schnell
  // typically returns in 10–15s. Returns {url, key} matching the upload-
  // image endpoint shape so the picker uses one preview-update path.
  generateCover: (workspaceId: string, blogPostId: string) =>
    api
      .post<{
        url: string;
        key: string;
      }>(`/api/workspaces/${workspaceId}/blog-posts/${blogPostId}/generate-cover`)
      .then((r) => r.data),
};

// V139 — read-side dashboard for blog page-view analytics pulled
// nightly from the customer's WordPress site via the plugin v0.30.
// All three endpoints return the same `BlogAnalyticsSummary` shape;
// per-post just carries the targeted post in a single-entry
// `topPosts` list.
export const blogAnalyticsApi = {
  summary: (workspaceId: string, brandId: string, from: string, to: string) =>
    api
      .get<BlogAnalyticsSummary>(`/api/workspaces/${workspaceId}/blog-analytics/summary`, {
        params: { brandId, from, to },
      })
      .then((r) => r.data),
  post: (workspaceId: string, blogPostId: string, from: string, to: string) =>
    api
      .get<BlogAnalyticsSummary>(
        `/api/workspaces/${workspaceId}/blog-analytics/posts/${blogPostId}`,
        { params: { from, to } },
      )
      .then((r) => r.data),
  topPosts: (workspaceId: string, brandId: string, from: string, to: string, limit = 10) =>
    api
      .get<BlogAnalyticsSummary>(`/api/workspaces/${workspaceId}/blog-analytics/top-posts`, {
        params: { brandId, from, to, limit },
      })
      .then((r) => r.data),
};

export interface GeoScoreSnapshot {
  id: string;
  brandId: string;
  /** V124 — null is the "insufficient data" sentinel. UI should
   *  render a "publish more posts to start scoring" card on null
   *  rather than showing a numeric score. */
  score: number | null;
  /** V124 rename — was `mentionCount`, always counted published
   *  posts. Renamed to match the column. */
  publishedPostCount: number;
  platformCount: number;
  consistencyPct: number;
  diversityPct: number;
  /** V124 — total brand-scope probes (Claude + Perplexity) inside
   *  the window. 0 for Trial/Starter brands without probe access. */
  probeCount: number;
  /** V124 — count of `brand_mentioned = true` probes. */
  probeMentionCount: number;
  /** V124 — `probeMentionCount / probeCount * 100`, or 0 when
   *  probeCount=0. Drives the new "AI mention rate" stat tile. */
  aiMentionRate: number;
  /** V143 — five-component v2 score breakdown. Null on pre-V143
   *  snapshots; the UI falls back to the legacy three-tile view in
   *  that case. */
  mentionRate?: number | null;
  citationRate?: number | null;
  engineCoverage?: number | null;
  sentimentScore?: number | null;
  competitorDelta?: number | null;
  totalProbes?: number;
  mentionedProbes?: number;
  windowDays: number;
  calculatedAt: string;
}

/** V143 — single brand URL cited by an AI engine. */
export interface CitationSummary {
  url: string;
  engine: 'perplexity' | 'gemini' | string;
  citationCount: number;
}

/** V143 — a probe query where competitors are cited but the brand isn't. */
export interface CitationGap {
  queryText: string;
  queryType: string;
  queryId: string | null;
  competitorDomains: string[];
  competitorCitations: number;
}

export interface ProbeCitation {
  sourceUrl: string;
  sourceDomain: string | null;
  position: number;
}

export interface ProbeResultItem {
  engine: string;
  queryType: string;
  queryText: string;
  brandMentioned: boolean;
  /** Legacy `positive | negative | not_found` (V61). */
  mentionType: string;
  mentionContext: string | null;
  responseText: string | null;
  probedAt: string | null;
  /** Phase 7e Step 1 — `positive | neutral | negative | not_mentioned`. */
  sentiment?: string | null;
  /** Phase 7e Step 1 — Haiku classifier confidence 0.0–1.0. */
  confidence?: number | null;
  /** Phase 7e Step 2 — Perplexity citation URLs in the order returned. */
  citations?: ProbeCitation[];
}

export interface GeoProbeResultSummary {
  totalProbes: number;
  positiveMentions: number;
  negativeMentions: number;
  notFound: number;
  mentionRate: number;
  byEngine: {
    engine: string;
    probes: number;
    mentions: number;
    negativeMentions: number;
    latestMention: string | null;
  }[];
  results: ProbeResultItem[];
}

// ── Phase 7e Step 3: Competitor benchmarking ────────────────────────────────

export interface BrandCompetitor {
  id: string;
  brandId: string;
  competitorName: string;
  competitorUrl: string | null;
  active: boolean;
  createdAt: string | null;
}

export interface BenchmarkEntry {
  name: string;
  /** null = brand's own row; non-null = competitor row */
  competitorId: string | null;
  /** engine → mention rate as a percentage (0..100) */
  mentionRate: Record<string, number>;
  /** counts per sentiment bucket: positive / neutral / negative / not_mentioned */
  sentimentBreakdown: Record<string, number>;
  /** Top-5 cited domains (Perplexity only) */
  citedDomains: string[];
  lastProbed: string | null;
  totalProbes: number;
}

export interface CompetitorBenchmarkResponse {
  brand: BenchmarkEntry;
  competitors: BenchmarkEntry[];
  periodDays: number;
}

export interface GeoScoreResponse {
  latest: GeoScoreSnapshot | null;
  history: GeoScoreSnapshot[];
  probes: GeoProbeResultSummary | null;
  /** Latest ad-hoc probe job — drives the "probe running…" banner. */
  latestProbeJob?: GeoProbeJobSummary | null;
}

export interface GeoProbeJobSummary {
  id: string;
  status: 'running' | 'completed' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  totalProbes: number;
  errorMessage: string | null;
}

export interface BrandPostMemory {
  id: string;
  brandId: string;
  platform: string;
  totalPosts: number;
  topTopics: string;
  recurringThemes: string | null;
  stylePatterns: string | null;
  topicsCovered: string;
  lastUpdated: string | null;
}

/**
 * Brand voice maturity widget response (Tier 3.9). `maturityScore` is
 * null when the brand has fewer than 3 published posts — frontend
 * renders an "insufficient data" card instead of the score ring.
 */
export interface VoiceMaturityResponse {
  maturityScore: number | null;
  publishedPostCount: number;
  postsNeededForFullVoice: number;
  hasThemes: boolean;
  hasStylePatterns: boolean;
  topicsCoveredCount: number;
  topTopics: string[];
  avgCitabilityScore: number | null;
  lastUpdated: string | null;
}
